import AIFillStructuredTableModal from 'notebook/ai-tools/flexible-data-entry/AIFillStructuredTableModal';
import * as NoteLinkedFileDAO from 'notebook/notes/attachments/NoteLinkedFileDAO';
// @ts-expect-error
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import NoteConstants from 'notebook/notes/NoteConstants';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {Row} from 'notebook/tables/TableConstants';
import {FromServerNoteLinkedFile, NotePart} from 'notebook/util/NoteUtilConstants';
import EntityStore from 'stores/EntityStore';
import runModal from 'ui-library/runModal';
import * as DateUtil from 'util/DateUtil';
import * as ImportUtil from 'util/ImportUtil';
import {withLoadingScreen} from 'util/Promises';

export interface AddStructuredTableRowsUsingAIResult {
  /**
   * New rows to add to the table. We ensure that these are valid, e.g. do not edit read-only columns.
   */
  newRows: {[tableName: string]: Array<Row>};
  /**
   * Note parts to append after the table. This is used to attach user-uploaded files if necessary.
   */
  noteParts: Array<NotePart>;
  cidIdMap: CidIdMap;
}

/**
 * Creates a file from prompt text and uploads it to S3
 * Returns the S3 location of the uploaded file
 */
export async function uploadPromptToS3(promptText: string): Promise<string> {
  const currentUser = EntityStore.currentUser();
  const dateOptions = {showYear: true, showTime: true};
  const dateString = DateUtil.readableTime(Date.now(), dateOptions);

  const formattedPromptText = promptText.startsWith('Example row:') ? `\n${promptText}` : promptText;

  const content = `\
User:
${currentUser.name}
${currentUser.email}

Time submitted:
${dateString}

User prompt:
${formattedPromptText}`;

  const promptFile = new File([content], `Data entry assistant record.txt`, {
    type: 'text/plain',
  });

  const s3Location = await ImportUtil.upload(promptFile);
  return s3Location!;
}

/**
 * Creates a note linked object part from a file and prepares it for the store
 */
function createNoteLinkedObject(file: FromServerNoteLinkedFile, cidIdMap: CidIdMap): NotePart {
  const notePart = {
    ...makeDefaultItem(NoteConstants.NOTE_LINKED_OBJECT),
    item_id: file.id,
    item: file,
    alignment: 'left',
    listType: null,
    placeholder: false,
  };
  NoteStoreTransforms.prepareNLOForStore(notePart, cidIdMap);
  return notePart;
}

/**
 * Creates all file attachments and note parts needed for the AI data entry
 */
async function createFileAttachments(
  promptText: string,
  fileS3Locations: Array<string>,
  tableNames: Array<string>
): Promise<{noteParts: Array<NotePart>; cidIdMap: CidIdMap}> {
  const cidIdMap = {};

  const options = {showYear: true, showTime: true};
  const text = `Data entry assistant upload for ${tableNames.join(', ')} on ${DateUtil.readableTime(Date.now(), options)}`;
  const textNotePart = makeDefaultItem(NoteConstants.TEXT, text, {shouldGenerateCid: false});
  NoteStoreTransforms.prepareNotePartForStore(textNotePart, cidIdMap);

  const promptFileS3Path = await uploadPromptToS3(promptText);
  const promptFileToAppend = (await NoteLinkedFileDAO.create({location: promptFileS3Path}))!;
  const promptFileNotePart = createNoteLinkedObject(promptFileToAppend, cidIdMap);

  const uploadedFilesToAppend = await Promise.all(
    fileS3Locations.map(async (s3Location) => (await NoteLinkedFileDAO.create({location: s3Location}))!)
  );
  const fileNoteParts = uploadedFilesToAppend.map((file) => createNoteLinkedObject(file, cidIdMap));

  return {
    noteParts: [textNotePart, promptFileNotePart, ...fileNoteParts],
    cidIdMap,
  };
}

/**
 * For a given structured table, prompt the user to add new rows, with details inferred by AI.
 *
 * Currently, we only allow adding rows, though in the future we may want to support edits and
 * other operations.
 *
 * Note that this currently should only be used for structured tables where column metadata is
 * enough to know if a cell is fillable by the user. See ai_data_entry_notebook_util.py on the
 * backend for details.
 */
export default async function addStructuredTableRowsUsingAI(
  entryId: string,
  structuredTables: Array<StructuredTable<unknown, unknown>>
): Promise<AddStructuredTableRowsUsingAIResult | null> {
  const modalResult = await runModal(AIFillStructuredTableModal, {entryId, structuredTables});
  if (modalResult == null) {
    return null;
  }

  const {noteParts, cidIdMap} = await withLoadingScreen(
    createFileAttachments(
      modalResult.promptText,
      modalResult.fileS3Locations,
      Object.keys(modalResult.newRows)
    ),
    'Attaching files...'
  );

  return {newRows: modalResult.newRows, noteParts, cidIdMap};
}
